import React, { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form'
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
} from '@/components/ui/dialog'
import { useChangePassword } from '../hooks/usePasswordChange'
import { Lock, Eye, EyeOff, CheckCircle, AlertCircle } from 'lucide-react'
import { useToast } from '@/hooks/use-toast'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { z } from 'zod'

// Password change schema
const changePasswordSchema = z.object({
    currentPassword: z.string().min(1, 'Current password is required'),
    newPassword: z.string()
        .min(8, 'New password must be at least 8 characters')
        .regex(/[A-Z]/, 'New password must contain at least one uppercase letter')
        .regex(/[a-z]/, 'New password must contain at least one lowercase letter')
        .regex(/[0-9]/, 'New password must contain at least one number')
        .regex(/[^a-zA-Z0-9]/, 'New password must contain at least one special character'),
    confirmPassword: z.string().min(1, 'Confirm password is required'),
}).refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match',
    path: ['confirmPassword'],
}).refine((data) => data.currentPassword !== data.newPassword, {
    message: 'New password cannot be the same as current password',
    path: ['newPassword'],
})

type ChangePasswordData = z.infer<typeof changePasswordSchema>

interface ChangePasswordDialogProps {
    open: boolean
    onOpenChange: (open: boolean) => void
}

export function ChangePasswordDialog({ open, onOpenChange }: ChangePasswordDialogProps) {
    const { t } = useTranslation()
    const { toast } = useToast()
    const changePassword = useChangePassword()
    const [showCurrentPassword, setShowCurrentPassword] = useState(false)
    const [showNewPassword, setShowNewPassword] = useState(false)
    const [showConfirmPassword, setShowConfirmPassword] = useState(false)

    const form = useForm<ChangePasswordData>({
        resolver: zodResolver(changePasswordSchema),
        defaultValues: {
            currentPassword: '',
            newPassword: '',
            confirmPassword: '',
        },
    })

    // Reset form when dialog opens
    React.useEffect(() => {
        if (open) {
            form.reset({
                currentPassword: '',
                newPassword: '',
                confirmPassword: '',
            })
        }
    }, [open, form])

    const newPassword = form.watch('newPassword')

    // Password strength checker
    const getPasswordStrength = (password: string) => {
        let score = 0
        const checks = {
            length: password.length >= 8,
            lowercase: /[a-z]/.test(password),
            uppercase: /[A-Z]/.test(password),
            number: /\d/.test(password),
            special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
        }

        score = Object.values(checks).filter(Boolean).length

        return {
            score,
            checks,
            strength: score < 2 ? 'weak' : score < 4 ? 'medium' : 'strong',
        }
    }

    const passwordStrength = getPasswordStrength(newPassword)

    const onSubmit = async (data: ChangePasswordData) => {
        try {
            await changePassword.mutateAsync({
                currentPassword: data.currentPassword,
                newPassword: data.newPassword,
            })

            form.reset()
            onOpenChange(false)
        } catch (error) {
            // Error handling is done in the hook
        }
    }

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="sm:max-w-[500px] max-h-[90vh] overflow-y-auto">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Lock className="h-5 w-5" />
                        {t('profile.changePassword')}
                    </DialogTitle>
                    <DialogDescription>
                        {t('profile.changePasswordDesc')}
                    </DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
                        {/* Security Notice */}
                        <Alert>
                            <AlertCircle className="h-4 w-4" />
                            <AlertDescription>
                                {t('profile.securityNote')}
                            </AlertDescription>
                        </Alert>

                        {/* Current Password */}
                        <FormField
                            control={form.control}
                            name="currentPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('profile.currentPassword')}</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                type={showCurrentPassword ? 'text' : 'password'}
                                                placeholder={t('profile.currentPasswordPlaceholder')}
                                                {...field}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                                            >
                                                {showCurrentPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* New Password */}
                        <FormField
                            control={form.control}
                            name="newPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('profile.newPassword')}</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                type={showNewPassword ? 'text' : 'password'}
                                                placeholder={t('profile.newPasswordPlaceholder')}
                                                {...field}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowNewPassword(!showNewPassword)}
                                            >
                                                {showNewPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        {/* Password Strength Indicator */}
                        {newPassword && (
                            <div className="space-y-2">
                                <Label className="text-sm font-medium">Password Strength</Label>
                                <div className="space-y-2">
                                    <div className="flex gap-2">
                                        {[1, 2, 3, 4, 5].map((level) => (
                                            <div
                                                key={level}
                                                className={`h-2 flex-1 rounded ${level <= passwordStrength.score
                                                    ? passwordStrength.strength === 'weak'
                                                        ? 'bg-red-500'
                                                        : passwordStrength.strength === 'medium'
                                                            ? 'bg-yellow-500'
                                                            : 'bg-green-500'
                                                    : 'bg-gray-200'
                                                    }`}
                                            />
                                        ))}
                                    </div>
                                    <div className="flex items-center gap-2">
                                        <span className={`text-sm font-medium ${passwordStrength.strength === 'weak'
                                            ? 'text-red-600'
                                            : passwordStrength.strength === 'medium'
                                                ? 'text-yellow-600'
                                                : 'text-green-600'
                                            }`}>
                                            {passwordStrength.strength.charAt(0).toUpperCase() + passwordStrength.strength.slice(1)}
                                        </span>
                                    </div>
                                </div>
                            </div>
                        )}

                        {/* Password Requirements */}
                        <div className="space-y-2">
                            <Label className="text-sm font-medium">{t('profile.passwordRequirements')}</Label>
                            <div className="space-y-1">
                                {[
                                    { key: 'length', text: t('profile.passwordRequirement1') },
                                    { key: 'lowercase', text: t('profile.passwordRequirement2') },
                                    { key: 'uppercase', text: t('profile.passwordRequirement3') },
                                    { key: 'number', text: t('profile.passwordRequirement4') },
                                ].map((requirement) => (
                                    <div key={requirement.key} className="flex items-center gap-2">
                                        {passwordStrength.checks[requirement.key as keyof typeof passwordStrength.checks] ? (
                                            <CheckCircle className="h-4 w-4 text-green-500" />
                                        ) : (
                                            <div className="h-4 w-4 rounded-full border-2 border-gray-300" />
                                        )}
                                        <span className={`text-sm ${passwordStrength.checks[requirement.key as keyof typeof passwordStrength.checks]
                                            ? 'text-green-600'
                                            : 'text-gray-500'
                                            }`}>
                                            {requirement.text}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Confirm Password */}
                        <FormField
                            control={form.control}
                            name="confirmPassword"
                            render={({ field }) => (
                                <FormItem>
                                    <FormLabel>{t('profile.confirmPassword')}</FormLabel>
                                    <FormControl>
                                        <div className="relative">
                                            <Input
                                                type={showConfirmPassword ? 'text' : 'password'}
                                                placeholder={t('profile.confirmPasswordPlaceholder')}
                                                {...field}
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="icon"
                                                className="absolute right-0 top-0 h-full px-3 py-2 hover:bg-transparent"
                                                onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                                            >
                                                {showConfirmPassword ? (
                                                    <EyeOff className="h-4 w-4" />
                                                ) : (
                                                    <Eye className="h-4 w-4" />
                                                )}
                                            </Button>
                                        </div>
                                    </FormControl>
                                    <FormMessage />
                                </FormItem>
                            )}
                        />

                        <DialogFooter>
                            <Button
                                type="button"
                                variant="outline"
                                onClick={() => onOpenChange(false)}
                            >
                                {t('profile.cancel')}
                            </Button>
                            <Button
                                type="submit"
                                disabled={changePassword.isPending || passwordStrength.score < 3}
                            >
                                <Lock className="h-4 w-4 mr-2" />
                                {changePassword.isPending ? t('common.saving') : t('profile.changePassword')}
                            </Button>
                        </DialogFooter>
                    </form>
                </Form>
            </DialogContent>
        </Dialog>
    )
}
